/* src/modules/peer/ChallengesHub.js */
import React, { useState, useEffect } from 'react';
import { 
    collection, query, where, orderBy, onSnapshot, addDoc, doc, updateDoc, 
    serverTimestamp, deleteDoc, arrayUnion, arrayRemove 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
    TrendingUpIcon, 
    PlusIcon, 
    CalendarIcon,
    TrophyIcon,
    TrashIcon,
    SparklesIcon 
} from '../../components/icons/Icons';

// --- CONFIGURATION ---
const BRAND_COLORS = {
    darkBlue: '#213368',
    teal: '#20BEC6',
    orange: '#F4A866',
    gold: '#FFD700',
    lightGrey: '#F3F4F6'
};

const formatDate = (val) => {
    if (!val) return '-';
    try {
        const date = val.toDate ? val.toDate() : new Date(val);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) { return '-'; }
};

const UserAvatar = ({ name, photoUrl, size = 'md' }) => {
    const sizeClasses = size === 'lg' ? 'h-16 w-16 text-2xl' : 'h-10 w-10 text-sm';
    if (photoUrl && photoUrl.length > 10) {
        return <img src={photoUrl} alt={name} className={`${sizeClasses} rounded-full object-cover border border-gray-200 shadow-sm`} referrerPolicy="no-referrer" onError={(e) => { e.target.style.display = 'none'; }} />;
    }
    return <div className={`${sizeClasses} rounded-full flex items-center justify-center font-bold shadow-sm`} style={{ backgroundColor: '#C9E9E6', color: '#213368' }}>{name?.charAt(0).toUpperCase() || 'U'}</div>;
};

// ===================================================================
// ## MAIN COMPONENT: ChallengesHub
// ===================================================================
export default function ChallengesHub({ userProfile, appId }) {
    const [view, setView] = useState('list'); // 'list' or 'leaderboard'
    const [selectedChallenge, setSelectedChallenge] = useState(null);
    const [challenges, setChallenges] = useState([]);
    const [users, setUsers] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const isAdmin = userProfile.role === 'Admin' || userProfile.department === 'HR';

    // 1. Fetch Challenges & Users
    useEffect(() => {
        const challRef = collection(db, `artifacts/${appId}/challenges`);
        const qChall = query(challRef, orderBy('createdAt', 'desc'));
        
        const usersRef = collection(db, `artifacts/${appId}/users`);
        const qUsers = query(usersRef, where('status', '==', 'Approved'));

        const unsubChall = onSnapshot(qChall, (snap) => {
            setChallenges(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsLoading(false);
        });

        const unsubUsers = onSnapshot(qUsers, (snap) => {
            setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => { unsubChall(); unsubUsers(); };
    }, [appId]);

    const handleOpenLeaderboard = (challenge) => {
        setSelectedChallenge(challenge);
        setView('leaderboard');
    };

    const handleDeleteChallenge = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm("Delete this challenge? This removes the leaderboard permanently.")) return;
        await deleteDoc(doc(db, `artifacts/${appId}/challenges`, id));
    };

    const handleToggleStatus = async (e, challenge) => {
        e.stopPropagation();
        const newStatus = challenge.status === 'Active' ? 'Completed' : 'Active';
        await updateDoc(doc(db, `artifacts/${appId}/challenges`, challenge.id), {
            status: newStatus
        });
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="space-y-6 animate-fadeIn">
            
            {/* --- VIEW 1: CHALLENGE LIST --- */}
            {view === 'list' && (
                <>
                    <div className="flex justify-between items-center bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <div>
                            <h3 className="text-2xl font-bold" style={{ color: BRAND_COLORS.darkBlue }}>Active Challenges</h3>
                            <p className="text-sm text-gray-500">Compete, earn points, and climb the leaderboard!</p>
                        </div>
                        {isAdmin && (
                            <button 
                                onClick={() => setIsModalOpen(true)}
                                className="flex items-center px-4 py-2 text-white font-medium rounded-lg hover:opacity-90 shadow-md transition"
                                style={{ backgroundColor: BRAND_COLORS.teal }}
                            >
                                <PlusIcon className="h-5 w-5 mr-2" /> Create Challenge
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {challenges.length === 0 ? (
                            <div className="col-span-full text-center py-12 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                                No challenges found. Admins can launch one!
                            </div>
                        ) : (
                            challenges.map(c => (
                                <div 
                                    key={c.id} 
                                    onClick={() => handleOpenLeaderboard(c)}
                                    className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-lg transition group relative ${c.status === 'Completed' ? 'opacity-75 bg-gray-50' : ''}`}
                                >
                                    {/* Admin Controls (Top Right) */}
                                    {isAdmin && (
                                        <div className="absolute top-4 right-4 flex items-center space-x-2 z-10" onClick={e => e.stopPropagation()}>
                                            
                                            {/* Status Toggle */}
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={c.status === 'Active'}
                                                    onChange={(e) => handleToggleStatus(e, c)}
                                                />
                                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                                                <span className="ml-2 text-xs font-medium text-gray-500 w-12">{c.status}</span>
                                            </label>

                                            {/* Delete Button */}
                                            <button 
                                                onClick={(e) => handleDeleteChallenge(e, c.id)}
                                                className="p-1 text-gray-300 hover:text-red-500 transition ml-2"
                                                title="Delete Permanently"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex items-center mb-4 mt-6">
                                        <div className={`p-3 rounded-full mr-4 ${c.status === 'Active' ? 'bg-blue-50 text-blue-600' : 'bg-gray-200 text-gray-500'}`}>
                                            <TrophyIcon className="h-8 w-8" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg text-gray-800 group-hover:text-blue-600 transition">{c.title}</h4>
                                            <p className="text-xs text-gray-500 flex items-center mt-1">
                                                <CalendarIcon className="h-3 w-3 mr-1" />
                                                {formatDate(c.startDate)} - {formatDate(c.endDate)}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{c.description}</p>
                                    
                                    {/* Footer: Goals & Awards */}
                                    <div className="space-y-2 pt-4 border-t border-gray-100">
                                        
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500 font-medium">Goal:</span>
                                            <span className="font-bold text-gray-800">{c.goal} Pts</span>
                                        </div>

                                        <div className="flex justify-between text-sm items-center">
                                            <span className="text-gray-500 font-medium flex items-center">
                                                <SparklesIcon className="h-3 w-3 mr-1 text-orange-400" />
                                                Prize:
                                            </span>
                                            <span className="font-bold text-[#F4A866] truncate ml-2 max-w-[150px]" title={c.award}>{c.award}</span>
                                        </div>

                                        <div className="pt-2 text-right">
                                             <span className="text-sm font-medium" style={{ color: BRAND_COLORS.teal }}>
                                                {c.winners && c.winners.length > 0 ? 'See Winners 🏆' : 'View Leaderboard →'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}

            {/* --- VIEW 2: LEADERBOARD --- */}
            {view === 'leaderboard' && selectedChallenge && (
                <ChallengeLeaderboard 
                    challenge={selectedChallenge} 
                    appId={appId} 
                    users={users}
                    isAdmin={isAdmin}
                    onBack={() => setView('list')} 
                />
            )}

            {/* --- ADMIN MODAL --- */}
            {isModalOpen && (
                <CreateChallengeModal 
                    appId={appId} 
                    currentUser={userProfile} 
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </div>
    );
}

// ===================================================================
// ## SUB-COMPONENT: Dynamic Leaderboard
// ===================================================================
function ChallengeLeaderboard({ challenge, appId, users, isAdmin, onBack }) {
    const [rankings, setRankings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsub = onSnapshot(doc(db, `artifacts/${appId}/challenges`, challenge.id), (docSnap) => {
            if (docSnap.exists()) {
                challenge.winners = docSnap.data().winners || [];
            }
        });
        return () => unsub();
    }, [appId, challenge]);

    useEffect(() => {
        const appRef = collection(db, `artifacts/${appId}/appreciations`);
        
        // Query by Tag (challengeId)
        const q = query(
            appRef, 
            where('challengeId', '==', challenge.id) 
        );

        const unsub = onSnapshot(q, (snapshot) => {
            const docs = snapshot.docs.map(d => d.data());
            const scores = {};
            
            docs.forEach(doc => {
                const userId = doc.toUserId || doc.toUserID || doc.touserid;
                const points = doc.pointsAwarded || 10;
                
                if (userId) {
                    if (!scores[userId]) scores[userId] = 0;
                    scores[userId] += points;
                }
            });

            const leaderboard = Object.entries(scores).map(([uid, score]) => {
                const user = users.find(u => u.id === uid) || { name: 'Unknown', photoURL: null };
                return {
                    uid,
                    name: user.name,
                    photoURL: user.photoURL,
                    score,
                    progress: Math.min((score / challenge.goal) * 100, 100)
                };
            }).sort((a, b) => b.score - a.score);

            setRankings(leaderboard);
            setLoading(false);
        });

        return () => unsub();
    }, [appId, challenge, users]);

    const handleCrownWinner = async (userId) => {
        const challengeRef = doc(db, `artifacts/${appId}/challenges`, challenge.id);
        const currentWinners = challenge.winners || [];
        
        if (currentWinners.includes(userId)) {
            await updateDoc(challengeRef, { winners: arrayRemove(userId) });
        } else {
            await updateDoc(challengeRef, { winners: arrayUnion(userId) });
        }
    };

    return (
        <div className="space-y-6">
            <button onClick={onBack} className="text-sm font-bold text-gray-500 hover:text-gray-800 flex items-center mb-4">
                &larr; Back to Challenges
            </button>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-8 text-center bg-blue-50 border-b border-blue-100">
                    <h2 className="text-3xl font-extrabold text-[#213368]">{challenge.title}</h2>
                    <p className="text-gray-600 mt-2 max-w-2xl mx-auto">{challenge.description}</p>
                    
                    <div className="flex justify-center items-center gap-8 mt-6">
                        <div className="text-center">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Goal</p>
                            <p className="text-2xl font-bold text-[#20BEC6]">{challenge.goal} Pts</p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Award</p>
                            <p className="text-xl font-bold text-[#F4A866]">{challenge.award}</p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Status</p>
                            <p className={`text-xl font-bold ${challenge.status === 'Active' ? 'text-green-600' : 'text-gray-500'}`}>
                                {challenge.status}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white">
                    {loading ? <LoadingSpinner /> : rankings.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">No points scored for this challenge yet.</div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">Progress</th>
                                    {isAdmin && <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Crown</th>}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {rankings.map((user, index) => {
                                    const isWinner = (challenge.winners || []).includes(user.uid);
                                    const rankColor = index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-orange-400' : 'text-gray-500';
                                    
                                    return (
                                        <tr key={user.uid} className={`transition ${isWinner ? 'bg-yellow-50' : 'hover:bg-blue-50'}`}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`text-xl font-bold ${rankColor}`}>#{index + 1}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <UserAvatar name={user.name} photoUrl={user.photoURL} />
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                                        {isWinner && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">🏆 Winner</span>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-bold text-gray-900">{user.score}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap align-middle">
                                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                    <div className={`h-2.5 rounded-full ${user.score >= challenge.goal ? 'bg-green-500' : 'bg-blue-600'}`} style={{ width: `${user.progress}%` }}></div>
                                                </div>
                                            </td>
                                            {isAdmin && (
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <button 
                                                        onClick={() => handleCrownWinner(user.uid)}
                                                        className={`p-2 rounded-full transition ${isWinner ? 'bg-yellow-400 text-white shadow-md' : 'text-gray-300 hover:text-yellow-400 hover:bg-gray-100'}`}
                                                        title="Click to crown as winner"
                                                    >
                                                        <TrophyIcon className="h-6 w-6" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

// ===================================================================
// ## SUB-COMPONENT: Admin Create Modal
// ===================================================================
function CreateChallengeModal({ appId, currentUser, onClose }) {
    const [form, setForm] = useState({ title: '', description: '', goal: 30, award: '', startDate: '', endDate: '' });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const newStart = new Date(form.startDate);
        const newEnd = new Date(form.endDate);

        if (newEnd <= newStart) {
            alert("End date must be after start date.");
            setSubmitting(false);
            return;
        }

        try {
            await addDoc(collection(db, `artifacts/${appId}/challenges`), {
                ...form,
                goal: Number(form.goal),
                startDate: newStart,
                endDate: newEnd,
                status: 'Active',
                winners: [],
                createdBy: currentUser.uid,
                createdAt: serverTimestamp()
            });
            onClose();
        } catch (error) {
            console.error(error);
            alert("Failed to create challenge.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
                <h3 className="text-xl font-bold text-[#213368] mb-4">Create New Challenge</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div><label className="block text-sm font-bold text-gray-700">Challenge Title</label><input required type="text" className="w-full p-2 border rounded" placeholder="e.g. October Sprint" onChange={e => setForm({...form, title: e.target.value})} /></div>
                    <div><label className="block text-sm font-bold text-gray-700">Description</label><textarea required className="w-full p-2 border rounded" rows="2" placeholder="What is this challenge about?" onChange={e => setForm({...form, description: e.target.value})} /></div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-sm font-bold text-gray-700">Point Goal</label><input required type="number" className="w-full p-2 border rounded" defaultValue={30} onChange={e => setForm({...form, goal: e.target.value})} /></div>
                        <div><label className="block text-sm font-bold text-gray-700">Award</label><input required type="text" className="w-full p-2 border rounded" placeholder="e.g. $50 Amazon Card" onChange={e => setForm({...form, award: e.target.value})} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-sm font-bold text-gray-700">Start Date</label><input required type="date" className="w-full p-2 border rounded" onChange={e => setForm({...form, startDate: e.target.value})} /></div>
                        <div><label className="block text-sm font-bold text-gray-700">End Date</label><input required type="date" className="w-full p-2 border rounded" onChange={e => setForm({...form, endDate: e.target.value})} /></div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                        <button type="submit" disabled={submitting} className="px-6 py-2 bg-[#20BEC6] text-white font-bold rounded hover:opacity-90">{submitting ? 'Launch Challenge' : 'Launch Challenge'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}